import { useEffect, useState, useMemo, Fragment } from "react";
import axios from "axios";
import { MailOpen, Mail, Trash2, ChevronDown, ChevronUp, UserPlus, CheckCircle2, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { SectionShell, Notice, LoadError } from "./ui";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  } catch {
    return iso;
  }
};

const SourceBadge = ({ source }) =>
  source === "whatsapp" ? (
    <span className="inline-flex items-center text-[10px] tracking-[0.14em] uppercase font-bold text-[#128C4A] bg-[#25D366]/12 border border-[#25D366]/30 px-2 py-1 whitespace-nowrap">
      WhatsApp
    </span>
  ) : (
    <span className="inline-flex items-center text-[10px] tracking-[0.14em] uppercase font-bold text-forest/70 bg-forest/8 border border-forest/20 px-2 py-1 whitespace-nowrap">
      Contact
    </span>
  );

const ReadPill = ({ read }) =>
  read ? (
    <span className="inline-flex items-center text-[10px] tracking-[0.14em] uppercase font-bold text-forest/55 bg-cream-3 border border-forest/15 px-2 py-1 whitespace-nowrap">
      Read
    </span>
  ) : (
    <span className="inline-flex items-center text-[10px] tracking-[0.14em] uppercase font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 whitespace-nowrap">
      Unread
    </span>
  );

const ActionIcons = ({ item, onToggleRead, onDelete, onConvert, converting, i }) => (
  <div className="flex gap-1">
    {!item.converted_customer_id && (
      <button
        data-testid={`enquiry-convert-${i + 1}`}
        onClick={() => onConvert(item)}
        disabled={converting}
        title="Convert to customer"
        className="p-2 text-forest/70 hover:text-forest transition-colors disabled:opacity-50"
      >
        <UserPlus size={16} />
      </button>
    )}
    <button
      data-testid={`enquiry-read-${i + 1}`}
      onClick={() => onToggleRead(item)}
      title={item.read ? "Mark as unread" : "Mark as read"}
      className="p-2 text-forest/70 hover:text-forest transition-colors"
    >
      {item.read ? <Mail size={16} /> : <MailOpen size={16} />}
    </button>
    <button
      data-testid={`enquiry-delete-${i + 1}`}
      onClick={() => onDelete(item)}
      title="Delete"
      className="p-2 text-red-700/70 hover:text-red-700 transition-colors"
    >
      <Trash2 size={16} />
    </button>
  </div>
);

const ConvertedBadge = ({ item, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 whitespace-nowrap"
    title={`Converted on ${fmtDate(item.converted_at)}`}
  >
    <CheckCircle2 size={11} strokeWidth={2} /> {item.converted_customer_name}
  </span>
);

export const EnquiriesSection = ({ focus }) => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [converting, setConverting] = useState(null); // enquiry id currently being converted
  const [duplicate, setDuplicate] = useState(null); // { enquiry, customer, matched_by }
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | unread | read | converted | whatsapp | contact

  const load = async () => {
    setLoadError("");
    const [enquiries, leads] = await Promise.all([
      axios.get(`${API}/admin/enquiries`, authHeaders()),
      axios.get(`${API}/admin/whatsapp-leads`, authHeaders()),
    ]);
    const merged = [
      ...enquiries.data.map((e) => ({ ...e, source: "contact" })),
      ...leads.data.map((l) => ({
        ...l,
        source: "whatsapp",
        read: l.read ?? false,
        name: `${l.first_name} ${l.last_name}`,
        phone: l.mobile,
        audience: "WhatsApp Lead",
        message: `Hi, I'm ${l.first_name} ${l.last_name}, my mobile is ${l.mobile} and email is ${l.email}. I'd like to get in touch.`,
      })),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setItems(merged);
  };

  useEffect(() => {
    load().catch((e) =>
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "The Enquiries list didn't load.")
    );
  }, []);

  // When navigated from the Dashboard, auto-expand the chosen enquiry and
  // scroll it into view once the list has loaded.
  useEffect(() => {
    if (!focus?.focusId || !items) return;
    setExpanded(focus.focusId);
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-row-id="${focus.focusId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [focus, items]);

  const endpoint = (item) => `${API}/admin/${item.source === "whatsapp" ? "whatsapp-leads" : "enquiries"}/${item.id}`;

  const toggleRead = async (item) => {
    await axios.patch(endpoint(item), { read: !item.read }, authHeaders());
    load();
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    await axios.delete(endpoint(item), authHeaders());
    load();
  };

  const convert = async (item, linkToCustomerId = null) => {
    setConverting(item.id);
    try {
      const payload = linkToCustomerId ? { link_to_customer_id: linkToCustomerId } : {};
      const { data } = await axios.post(
        `${API}/admin/enquiries/${item.id}/convert`,
        payload,
        authHeaders()
      );
      setDuplicate(null);
      await load();
      toast.success(
        data.linked
          ? `Linked to existing customer: ${data.customer.name}`
          : `Converted to customer: ${data.customer.name}`
      );
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === "object" && detail.code === "customer_exists") {
        // Duplicate — ask admin whether to link the enquiry to the existing customer.
        setDuplicate({ enquiry: item, customer: detail.customer, matched_by: detail.matched_by || [] });
      } else {
        toast.error(formatApiErrorDetail(detail) || "Could not convert enquiry.");
      }
    } finally {
      setConverting(null);
    }
  };

  const unread = items?.filter((i) => !i.read).length ?? 0;

  const filtered = useMemo(() => {
    if (!items) return items;
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter === "unread" && it.read) return false;
      if (statusFilter === "read" && !it.read) return false;
      if (statusFilter === "converted" && !it.converted_customer_id) return false;
      if (statusFilter === "whatsapp" && it.source !== "whatsapp") return false;
      if (statusFilter === "contact" && it.source !== "contact") return false;
      if (!q) return true;
      const hay = `${it.name || ""} ${it.email || ""} ${it.phone || ""} ${it.message || ""} ${it.audience || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, statusFilter]);

  return (
    <SectionShell
      title="Enquiries"
      subtitle={items ? `${items.length} received · ${unread} unread — contact form and WhatsApp leads together` : "Contact form and WhatsApp leads from the website."}
      testid="admin-enquiries-section"
    >
      <Notice error={error} />
      {loadError ? (
        <LoadError testid="enquiries-load-error" onRetry={() => load().catch((e) => setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "The Enquiries list didn't load."))} message={loadError} />
      ) : !items ? (
        <p className="text-sm text-sage">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-sage bg-white border border-forest/10 px-6 py-10 text-center">No enquiries yet.</p>
      ) : (
        <>
          {/* Search + status filter */}
          <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="enquiries-filter-bar">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="enquiries-search-input"
                placeholder="Search by name, email, phone or message…"
                className="w-full bg-white border border-forest/15 pl-9 pr-3 py-2 text-[13px] focus:border-gold focus:outline-none"
              />
            </div>
            <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.18em] uppercase text-forest/60"><Filter size={12} strokeWidth={1.8} /> Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="enquiries-status-filter"
              className="bg-white border border-forest/15 px-3 py-1.5 text-[12px] focus:border-gold focus:outline-none"
            >
              <option value="all">{`All (${items.length})`}</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
              <option value="converted">Converted</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="contact">Contact form</option>
            </select>
            {filtered.length !== items.length && (
              <span className="text-[11px] text-sage">Showing {filtered.length} of {items.length}</span>
            )}
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-sage bg-white border border-forest/10 px-6 py-10 text-center" data-testid="enquiries-empty-filtered">
              No enquiries match your search.
            </p>
          ) : (
          <>
          {/* Desktop / tablet-landscape table (lg+) */}
          <div className="hidden lg:block bg-white border border-forest/10">
            <table className="w-full table-fixed text-left text-[13px]">
              <colgroup>
                <col className="w-24" />
                <col className="w-24" />
                <col className="w-[11%]" />
                <col className="w-[16%]" />
                <col className="w-[11%]" />
                <col className="w-[13%]" />
                <col className="w-[9%]" />
                <col />
                <col className="w-44" />
              </colgroup>
              <thead>
                <tr className="border-b border-forest/10 text-[10.5px] tracking-[0.18em] uppercase text-forest/60">
                  <th className="px-3 py-4 font-bold">Status</th>
                  <th className="px-2 py-4 font-bold">Source</th>
                  <th className="px-3 py-4 font-bold">Name</th>
                  <th className="px-3 py-4 font-bold">Email</th>
                  <th className="px-3 py-4 font-bold">Phone</th>
                  <th className="px-3 py-4 font-bold">Type</th>
                  <th className="px-3 py-4 font-bold">Received</th>
                  <th className="px-3 py-4 font-bold">Message</th>
                  <th className="px-3 py-4 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, i) => {
                  const isOpen = expanded === item.id;
                  return (
                    <Fragment key={item.id}>
                      <tr
                        data-testid={`enquiry-row-${i + 1}`}
                        data-row-id={item.id}
                        className={`border-b border-forest/8 ${item.read ? "text-sage" : "text-ink font-medium"}`}
                      >
                        <td className="px-3 py-4 align-top">
                          <ReadPill read={item.read} />
                        </td>
                        <td className="px-2 py-4 align-top"><SourceBadge source={item.source} /></td>
                        <td className="px-3 py-4 align-top truncate" title={item.name}>{item.name}</td>
                        <td className="px-3 py-4 align-top truncate" title={item.email}>{item.email}</td>
                        <td className="px-3 py-4 align-top truncate" title={item.phone || "—"}>{item.phone || "—"}</td>
                        <td className="px-3 py-4 align-top truncate" title={item.audience}>{item.audience}</td>
                        <td className="px-3 py-4 align-top whitespace-nowrap">{fmtDate(item.created_at)}</td>
                        <td className="px-3 py-4 align-top">
                          <p className="truncate text-ink/80" title={item.message}>
                            {item.message}
                          </p>
                        </td>
                        <td className="px-3 py-4 align-top text-right">
                          <div className="flex items-center justify-end gap-3">
                            {item.converted_customer_id ? (
                              <ConvertedBadge item={item} testid={`enquiry-converted-badge-${i + 1}`} />
                            ) : (
                              <button
                                data-testid={`enquiry-convert-link-${i + 1}`}
                                onClick={() => convert(item)}
                                disabled={converting === item.id}
                                className="inline-flex items-center gap-1 text-forest hover:text-gold-dark transition-colors text-[12px] font-semibold disabled:opacity-50"
                                title="Convert to customer"
                              >
                                <UserPlus size={13} />
                                {converting === item.id ? "…" : "Convert"}
                              </button>
                            )}
                            <button
                              data-testid={`enquiry-expand-${i + 1}`}
                              onClick={() => setExpanded(isOpen ? null : item.id)}
                              className="inline-flex items-center gap-1 text-forest hover:text-gold-dark transition-colors text-[12px] font-semibold"
                            >
                              {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              {isOpen ? "Hide" : "View"}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-forest/8 bg-cream-3">
                          <td colSpan={9} className="px-5 py-5">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                              <p className="text-[13px] leading-relaxed text-ink/80 whitespace-pre-wrap flex-1 min-w-0 max-w-3xl">
                                {item.message}
                              </p>
                              <ActionIcons item={item} onToggleRead={toggleRead} onDelete={remove} onConvert={convert} converting={converting === item.id} i={i} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile / tablet-portrait cards (< lg) */}
          <div className="lg:hidden space-y-3">
            {filtered.map((item, i) => {
              const isOpen = expanded === item.id;
              return (
                <div
                  key={item.id}
                  data-testid={`enquiry-card-${i + 1}`}
                  data-row-id={item.id}
                  className={`bg-white border border-forest/10 p-4 ${item.read ? "" : "border-l-4 border-l-gold"}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <ReadPill read={item.read} />
                      <SourceBadge source={item.source} />
                      {item.converted_customer_id && <ConvertedBadge item={item} testid={`enquiry-converted-badge-card-${i + 1}`} />}
                    </div>
                    <span className="text-[11px] text-sage whitespace-nowrap">{fmtDate(item.created_at)}</span>
                  </div>
                  <p className={`text-[15px] ${item.read ? "text-sage" : "text-ink font-semibold"}`}>{item.name}</p>
                  <p className="text-[12.5px] text-sage mt-0.5 break-all">{item.email}</p>
                  {item.phone && <p className="text-[12.5px] text-sage break-all">{item.phone}</p>}
                  <p className="text-[11px] tracking-[0.12em] uppercase text-forest/60 mt-2 font-bold">{item.audience}</p>
                  <p className={`text-[13px] text-ink/80 mt-2 leading-relaxed ${isOpen ? "" : "line-clamp-2"}`}>
                    {item.message}
                  </p>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <button
                      data-testid={`enquiry-expand-card-${i + 1}`}
                      onClick={() => setExpanded(isOpen ? null : item.id)}
                      className="inline-flex items-center gap-1 text-forest text-[12px] font-semibold"
                    >
                      {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      {isOpen ? "Hide" : "View"}
                    </button>
                    <ActionIcons item={item} onToggleRead={toggleRead} onDelete={remove} onConvert={convert} converting={converting === item.id} i={i} />
                  </div>
                </div>
              );
            })}
          </div>
          </>
          )}
        </>
      )}

      {duplicate && (
        <div
          data-testid="enquiry-duplicate-modal"
          className="fixed inset-0 z-50 bg-forest/50 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setDuplicate(null)}
        >
          <div
            className="bg-cream max-w-md w-full p-7 border border-forest/15 shadow-[0_30px_80px_rgba(0,0,0,0.3)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-2xl text-forest mb-2">Customer already exists</h3>
            <p className="text-[13.5px] text-ink/75 leading-relaxed mb-5">
              A customer with the same {duplicate.matched_by.join(" and ") || "contact details"} already exists:
            </p>
            <div className="bg-white border border-forest/15 px-4 py-3 mb-6">
              <p className="font-serif text-lg text-forest">{duplicate.customer.name}</p>
              {duplicate.customer.email && <p className="text-[12.5px] text-sage mt-0.5 break-all">{duplicate.customer.email}</p>}
              {duplicate.customer.phone && <p className="text-[12.5px] text-sage break-all">{duplicate.customer.phone}</p>}
            </div>
            <p className="text-[12.5px] text-sage leading-relaxed mb-5">
              Link this enquiry to the existing customer? The enquiry message will be appended to their notes.
            </p>
            <div className="flex gap-3 flex-wrap">
              <button
                data-testid="enquiry-duplicate-link"
                onClick={() => convert(duplicate.enquiry, duplicate.customer.id)}
                disabled={converting === duplicate.enquiry.id}
                className="btn-gold !py-3 disabled:opacity-60"
              >
                {converting === duplicate.enquiry.id ? "Linking…" : "Link to existing"}
              </button>
              <button
                data-testid="enquiry-duplicate-cancel"
                onClick={() => setDuplicate(null)}
                className="btn-ghost-dark"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </SectionShell>
  );
};
